import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useDeliveryService from '../../../app/services/hooks/useDeliveryService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { DeliveryShow } from '../../../app/models/Delivery';
import { ServiceResponse } from '../../../app/services/shared/interfaces';

import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import DeliveryDetailContainer from '../components/DeliveryDetailContainer';

type RouterParams = {
    id: string;
};

const toDateInputValue = (d: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const DeliveryView = () => {
    const { id } = useParams<RouterParams>();
    const deliveryId = parseInt(id);

    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    const { showDelivery, fetchingShowDelivery, changeDeliveryStatus } = useDeliveryService();

    const [delivery, setDelivery] = useState<DeliveryShow | undefined>(undefined);
    const [changingStatus, setChangingStatus] = useState(false);
    const [showDeliveredModal, setShowDeliveredModal] = useState(false);
    const [actualDeliveryDate, setActualDeliveryDate] = useState(() => toDateInputValue(new Date()));

    const loadDelivery = () => {
        if (showLoading) showLoading('loading', 'Cargando entrega...');
        showDelivery(deliveryId, {
            onSuccess: (response: ServiceResponse) => {
                setDelivery(response.data.delivery);
                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                navigationPage('/deliveries');
            }
        });
    };

    useEffect(() => {
        loadDelivery();
    }, [deliveryId]);

    const breadcrumbs: BreadcrumbsItem[] = [
        { name: 'Entregas', url: '/deliveries', isActive: false },
        { name: `Ver #${id}`, url: `/deliveries/${id}/view`, isActive: true }
    ];

    const handleChangeStatus = (newStatus: string) => {
        const statusLabels: Record<string, string> = {
            PENDING: 'Pendiente',
            IN_TRANSIT: 'En Tránsito',
            DELIVERED: 'Entregada'
        };

        if (newStatus === 'DELIVERED') {
            setActualDeliveryDate(toDateInputValue(new Date()));
            setShowDeliveredModal(true);
            return;
        }

        useReactConfirmAlert().requestConfirmation({
            title: '¿Cambiar estado?',
            message: `¿Está seguro de cambiar el estado a "${statusLabels[newStatus]}"?`,
            buttons: {
                confirmButton: {
                    label: 'Sí, cambiar',
                    onClick: () => doChangeStatus(newStatus),
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => { /* cancelar sin acción */ }
                }
            }
        });
    };

    const doChangeStatus = (newStatus: string, actualDeliveryDateStr?: string) => {
        setChangingStatus(true);
        if (showLoading) showLoading('loading', 'Cambiando estado...');

        const payload = newStatus === 'DELIVERED' && actualDeliveryDateStr
            ? { actual_delivery_date: actualDeliveryDateStr }
            : undefined;

        changeDeliveryStatus(deliveryId, newStatus, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setChangingStatus(false);
                setShowDeliveredModal(false);
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                toast.success(response.message);
                loadDelivery();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setChangingStatus(false);
                setShowDeliveredModal(false);
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
            }
        }, payload);
    };

    const confirmDelivered = () => {
        const dateStr = actualDeliveryDate || toDateInputValue(new Date());
        doChangeStatus('DELIVERED', dateStr);
    };

    if (fetchingShowDelivery || !delivery) {
        return <LazyLoading height="300" />;
    }

    const getStatusButtons = () => {
        const currentStatus = delivery.status;
        const buttons = [];

        if (currentStatus === 'PENDING') {
            buttons.push(
                <button
                    key="in-transit"
                    className="btn btn-info btn-sm me-2"
                    onClick={() => handleChangeStatus('IN_TRANSIT')}
                    disabled={changingStatus}
                >
                    <i className="mdi mdi-truck"></i> Marcar En Tránsito
                </button>
            );
        }

        if (currentStatus === 'IN_TRANSIT' || currentStatus === 'PENDING') {
            buttons.push(
                <button
                    key="delivered"
                    className="btn btn-success btn-sm"
                    onClick={() => handleChangeStatus('DELIVERED')}
                    disabled={changingStatus}
                >
                    <i className="mdi mdi-check"></i> Marcar Entregada
                </button>
            );
        }

        return buttons;
    };

    const deliveredModal = showDeliveredModal && (
            <>
                <div className="modal-backdrop fade show" />
                <div className="modal fade show" style={{ display: 'block', zIndex: 1055 }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar entrega</h5>
                                <button type="button" className="btn-close" onClick={() => setShowDeliveredModal(false)} aria-label="Cerrar" />
                            </div>
                            <div className="modal-body">
                                <p className="mb-2">Se registrará la fecha real de la entrega (día, mes y año). El sistema sugiere la fecha actual; puede ajustarla si lo desea.</p>
                                <label className="form-label">Fecha real de entrega</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={actualDeliveryDate}
                                    onChange={(e) => setActualDeliveryDate(e.target.value)}
                                />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowDeliveredModal(false)}>Cancelar</button>
                                <button type="button" className="btn btn-success" onClick={confirmDelivered} disabled={changingStatus}>Confirmar</button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );

    return (
        <>
            <Breadcrumbs pageSection={`Entrega #${id}`} breadcrumbs={breadcrumbs} />

            {/* Sección de Datos Generales */}
            <DefaultCard>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="h3 mt-0 mb-0 card-title">Datos Generales</div>
                    <div>{getStatusButtons()}</div>
                </div>

                {/* Pedido */}
                <div className="mb-2 row">
                    <div className="col-md-12">
                        <label className="col-form-label">Pedido</label>
                        <input
                            type="text"
                            className="form-control"
                            value={
                                typeof delivery.sales_order === 'object' && delivery.sales_order?.display
                                    ? delivery.sales_order.display
                                    : typeof delivery.sales_order === 'string'
                                    ? delivery.sales_order
                                    : 'N/A'
                            }
                            disabled={true}
                        />
                    </div>
                </div>

                {/* Sociedad */}
                <div className="mb-2 row">
                    <div className="col-md-6">
                        <label className="col-form-label">Rut Sociedad</label>
                        <input
                            type="text"
                            className="form-control"
                            value={delivery.society ? delivery.society.split('<br/>')[0].trim() : 'N/A'}
                            disabled={true}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="col-form-label">Razón social sociedad</label>
                        <input
                            type="text"
                            className="form-control"
                            value={delivery.society ? delivery.society.split('<br/>')[1]?.trim() || 'N/A' : 'N/A'}
                            disabled={true}
                        />
                    </div>
                </div>

                {/* Cliente */}
                <div className="mb-2 row">
                    <div className="col-md-6">
                        <label className="col-form-label">Rut Cliente</label>
                        <input
                            type="text"
                            className="form-control"
                            value={delivery.client ? delivery.client.split('<br/>')[0].trim() : 'N/A'}
                            disabled={true}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="col-form-label">Razón social cliente</label>
                        <input
                            type="text"
                            className="form-control"
                            value={delivery.client ? delivery.client.split('<br/>')[1]?.trim() || 'N/A' : 'N/A'}
                            disabled={true}
                        />
                    </div>
                </div>

                <hr />

                {/* Destino y Fecha de Entrega */}
                <div className="mb-2 row">
                    <div className="col-md-6">
                        <label className="col-form-label">Destino</label>
                        <input
                            type="text"
                            className="form-control"
                            value={delivery.destination || 'N/A'}
                            disabled={true}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="col-form-label">Fecha de entrega</label>
                        <input
                            type="date"
                            className="form-control"
                            value={delivery.delivery_date || ''}
                            disabled={true}
                        />
                        {(delivery.actual_delivery_date || delivery.actual_delivery_registered_at) && (
                            <div className="mt-2">
                                {delivery.actual_delivery_date && (
                                    <>
                                        <label className="col-form-label text-muted small mb-0">Fecha real de entrega</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={delivery.actual_delivery_date ? new Date(delivery.actual_delivery_date).toLocaleDateString('es-CL') : ''}
                                            disabled={true}
                                            style={{ backgroundColor: '#f8f9fa' }}
                                        />
                                    </>
                                )}
                                {delivery.actual_delivery_registered_at && (
                                    <>
                                        <label className="col-form-label text-muted small mb-0 mt-1">Registrado el</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={new Date(delivery.actual_delivery_registered_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'medium' })}
                                            disabled={true}
                                            style={{ backgroundColor: '#f8f9fa' }}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Estado e Inventario Procesado */}
                <div className="mb-2 row">
                    <div className="col-md-6">
                        <label className="col-form-label">Estado</label>
                        <div>
                            <span className={`badge bg-${
                                delivery.status === 'DELIVERED' ? 'success' :
                                delivery.status === 'IN_TRANSIT' ? 'info' : 'warning'
                            }`} style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}>
                                {delivery.status_display}
                            </span>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <label className="col-form-label">Inventario Procesado</label>
                        <div>
                            <span className={`badge bg-${delivery.is_inventory_processed ? 'success' : 'secondary'}`} style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}>
                                {delivery.is_inventory_processed ? 'SÍ' : 'NO'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Observaciones */}
                <div className="mb-2 row">
                    <div className="col-md-12">
                        <label className="col-form-label">Observaciones</label>
                        <textarea
                            className="form-control"
                            value={delivery.observations || ''}
                            disabled={true}
                            rows={3}
                        />
                    </div>
                </div>

                {/* Información de Auditoría */}
                <hr />
                <div className="mb-2 row">
                    <div className="col-md-6">
                        <label className="col-form-label">Creado Por</label>
                        <input
                            type="text"
                            className="form-control"
                            value={
                                delivery.created_user
                                    ? `[Admin] ${delivery.created_user.first_name} ${delivery.created_user.last_name}`
                                    : 'N/A'
                            }
                            disabled={true}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="col-form-label">Fecha Creación</label>
                        <input
                            type="text"
                            className="form-control"
                            value={delivery.created_at ? new Date(delivery.created_at).toLocaleString('es-CL', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: true
                            }) : 'N/A'}
                            disabled={true}
                        />
                    </div>
                </div>
            </DefaultCard>

            {/* Sección de Lotes */}
            <DeliveryDetailContainer deliveryId={deliveryId} onUpdate={loadDelivery} />

            {deliveredModal}
        </>
    );
};

export default DeliveryView;

